import React, { useEffect } from 'react';
import '../../App.css';
import './data-management.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch, useSelectedCanfar } from '../../app/hooks';

// icons
import { FolderUp } from 'lucide-react';

// import responsive layout items.
import { Col, Row } from 'react-bootstrap';

// types
import {
  CanfarServiceType,
  DataItemType,
  FileBrowserPage,
  JobType,
  Site,
} from '../../utils/types';
import {
  Event,
  EventType,
  EventTarget,
  InitiateDMSearchParams,
} from '../../utils/events';

// import styles.
import {
  customStylesDark,
  customStylesLight,
} from '../../utils/custom-styles-select-box';

// functions
import { ContainerHook, siteName } from '../../utils/functions';
import { handleKeyboardActivation } from '../../utils/accessibility';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { CheckBox } from '../../tools/controls';
import DataManagementTable from './data-management-table';
import { displayFileSize } from '../../utils/functions';
import EditableSelect from '../../tools/editable-select';
import { ContextMenu } from '../menus/context-menu';
import DataStagingComponent from './data-staging-component';
import CollapsingPanel, {
  PanelPosition,
} from '../../tools/collapsing-panel/collapsing-panel';
import MiniButton from '../../tools/mini-button';
import SiteIcon from '../../tools/site-icon';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions (redux).
import {
  setContextMenuRefresh,
  setFilenameFilter,
  setLifetimeStr,
  setLifetime,
  setShowHiddenFiles,
  setSiteExpanded,
} from './file-browser-slice';
import { getRucioSites } from './file-browser-actions';
import { rG_setSite } from '../../app/global-slice';
import { fnD_buildRucioLocations } from '../../app/data-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_CONTAINERS,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
  ctx_menuDisplayed,
} from '../menus/context-menu-actions';
import { setUserDropdownMenuDisplayed } from '../../app-slice';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function DataManagement(props: {
  dataManagementJobs: JobType[];
  jobsLoading: boolean;
  tabVisible: boolean;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sContextMenuRefresh: number = useAppSelector(
    (state) => state.fileBrowser.contextMenuRefresh,
  );

  // global state parameters.
  const sG_selectedCanfar: CanfarServiceType | undefined = useSelectedCanfar();
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_username: string | undefined = useAppSelector(
    (state) => state.global.username,
  );
  const sG_preferredUsername: string | undefined = useAppSelector(
    (state) => state.global.preferredUsername,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_site: Site | undefined = useAppSelector((state) => state.global.site);
  const sG_narrowScreen: boolean = useAppSelector((state) => state.global.narrowScreen);

  // data state parameters.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);

  // current page displayed.
  const sPageDisplayed: FileBrowserPage = useAppSelector(
    (state) => state.fileBrowser.pageParams.pageDisplayed,
  );

  // filter values.
  const sFilenameFilter: string[] = useAppSelector(
    (state) => state.fileBrowser.pageParams.filenameFilter,
  );
  const sFileTypeFilter: string = useAppSelector(
    (state) => state.fileBrowser.pageParams.fileTypeFilter,
  );
  const sShowHiddenFiles: boolean = useAppSelector(
    (state) => state.fileBrowser.pageParams.showHiddenFiles,
  );

  // lifetime.
  const sLifetimeStr: string = useAppSelector((state) => state.fileBrowser.lifetimeStr);

  // table state.
  const sNamespace: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.namespace,
  );
  const sJobID: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.jobID,
  );
  const sDataset: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.dataset,
  );
  const sCollection: { id: number; name: string } | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.collection,
  );

  // user and project storage paths.
  const sStoragePath: string[] = useAppSelector(
    (state) => state.fileBrowser.pageParams.storagePath,
  );

  // selected items.
  const sSelectedItems: DataItemType[] = useAppSelector(
    (state) => state.fileBrowser.selectedItems,
  );

  // pop-up site entry box.
  const sSiteExpanded: boolean = useAppSelector(
    (state) => state.fileBrowser.siteExpanded,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // create a list of site option, based upon the list of sites.
  const _siteOptions: {
    value: string;
    label: string;
    icon?: string;
  }[] = sD_sites.map((site) => ({
    value: site.site_uuid ?? '',
    label: siteName({ site: site }),
    icon: site.icon /*site.icon === undefined ? '' : site.icon,*/,
  }));

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({
    container: CONTEXT_CONTAINERS.FILE_BROWSER_WHOLE_WINDOW,
  });

  // retrieve the list of sites (in availability order) for the project
  // items and selected project items.
  const selectedItemSites: {
    site: Site;
    count: number;
  }[] = getRucioSites({
    allItems: [],
    selectedItems: sSelectedItems,
    catalog: 'file-browser',
  });

  // When viewing job details, the panel must show only the destination
  // site of that specific job — not whichever Rucio site happens to sort first.
  const computePanelSites: Site[] = (() => {
    if (sPageDisplayed === FileBrowserPage.JobDetails && sJobID !== undefined) {
      const job = props.dataManagementJobs.find((j) => j.job_id === sJobID);
      if (job?.to_site_uuid) {
        const site = sD_sites.find((s) => s.site_uuid === job.to_site_uuid);
        if (site !== undefined) return [site];
      }
    }
    return selectedItemSites.length > 0 ? [selectedItemSites[0].site] : [];
  })();

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    if (inputBox.name === 'lifetime') {
      dispatch(setLifetimeStr(inputBox.value));
    }
    if (inputBox.name === 'showHiddenFiles') {
      //
      dispatch(setShowHiddenFiles(inputBox.checked));

      if (
        sPageDisplayed === FileBrowserPage.UserStorage ||
        sPageDisplayed === FileBrowserPage.ProjectStorage
      ) {
        //
        // run the data-management search.
        const params: InitiateDMSearchParams = {
          filename: sFilenameFilter.length >= 2 ? sFilenameFilter[1] : '',
          fileType: sFileTypeFilter,
          preferredUsername: sG_preferredUsername,
          storagePath: sStoragePath,
          canfarService: sG_selectedCanfar,
          showHiddenFiles: inputBox.checked,
          pageDisplayed: sPageDisplayed,
        };
        const newEvent: Event = {
          eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
          eventType: EventType.INITIATE_DM_SEARCH,
          parameters: params,
        };
        dispatch(
          handleEvent({
            event: newEvent,
          }),
        );
        //
      }
      //
    }
    //
  }; // inputHandler

  //	------------------------------------------------------------
  //
  //	Restrict input box entry to numbers only, plus . , + -
  //
  //	------------------------------------------------------------

  const numericKeysOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    //
    if (
      (event.charCode < 48 || event.charCode > 57) &&
      (event.charCode < 43 || event.charCode > 46)
    ) {
      event.preventDefault();
    }
    //
  }; // numericKeysOnly

  //	------------------------------------------------------------
  //
  //	Handler for lost-focus events on the input boxes.
  //
  //	------------------------------------------------------------

  const onBlurEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    if (inputBox.name === 'lifetime') {
      //
      // params.
      let lifetimeNumeric: number | undefined = Number(sLifetimeStr);
      if (isNaN(lifetimeNumeric)) {
        dispatch(setLifetime(3600.0));
      } else {
        dispatch(setLifetime(lifetimeNumeric ?? 3600.0));
      }
      //
    }
    //
  }; // onBlurEvent

  //	------------------------------------------------------------
  //
  //	close the context menus when the user clicks anywhere
  //	in the container.
  //
  //	------------------------------------------------------------

  function onClickDivHandler(event: React.MouseEvent<HTMLDivElement>) {
    //
    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
        visible: false,
      }),
    );
    dispatch(setContextMenuRefresh(Date.now()));
    //
  } // onClickDivHandler

  //	------------------------------------------------------------
  //
  //	Handler for div clicks.
  //
  //	------------------------------------------------------------

  const handleNavigationSelection = (targetId: string) => {
    var namespace: string | undefined = undefined;
    var filename: string | undefined = undefined;
    var pageDisplayed: FileBrowserPage = FileBrowserPage.FileBrowser;
    var storagePath: string[] = [];
    let cacheLevel: number = -1;

    if (targetId === 'dataManagement') {
      cacheLevel = 0;
    }
    if (targetId === 'namespaces') {
      pageDisplayed = FileBrowserPage.Namespaces;
      cacheLevel = 1;
    }
    if (targetId === 'collections') {
      pageDisplayed = FileBrowserPage.Collections;
      cacheLevel = 1;
    }
    if (targetId === 'items') {
      namespace = sNamespace;
      pageDisplayed = FileBrowserPage.Items;
      cacheLevel = 2;
    }

    // if the user has clicked on a directory path, then update the path
    // accordingly.
    let storagePathChanged: boolean = false;
    if (targetId.startsWith('userStorage_')) {
      const levelStr: string = targetId.substring('userStorage_'.length);
      let level: number = -1;
      try {
        level = Number(levelStr);
      } catch (e) {}

      storagePath = sStoragePath.slice();
      if (level > -1) {
        storagePath.splice(level, storagePath.length - level);
      }

      cacheLevel = 1 + level;

      storagePathChanged = true;
      pageDisplayed = FileBrowserPage.UserStorage;
    }

    if (targetId.startsWith('projectStorage_')) {
      const levelStr: string = targetId.substring('projectStorage_'.length);
      let level: number = -1;
      try {
        level = Number(levelStr);
      } catch (e) {}

      storagePath = sStoragePath.slice();
      if (level > -1) {
        storagePath.splice(level, storagePath.length - level);
      }

      cacheLevel = 1 + level;

      storagePathChanged = true;
      pageDisplayed = FileBrowserPage.ProjectStorage;
    }

    // remove part of the cache.
    if (cacheLevel > -1) {
      let filterCache: string[] = sFilenameFilter.slice();
      filterCache.splice(cacheLevel, filterCache.length - cacheLevel);
      dispatch(setFilenameFilter(filterCache));
      if (cacheLevel > 0 && filterCache[cacheLevel - 1] !== '') {
        filename = filterCache[cacheLevel - 1];
      }
    }

    if (
      targetId === 'dataManagement' ||
      targetId === 'namespaces' ||
      targetId === 'collections' ||
      targetId === 'items' ||
      storagePathChanged === true
    ) {
      // run the data-management search.
      // FIXME: Need a dynamic way to switch between user and project items
      const params: InitiateDMSearchParams = {
        namespace: namespace,
        project: sG_project,
        filename: filename,
        preferredUsername: sG_preferredUsername,
        storagePath: storagePath,
        canfarService: sG_selectedCanfar,
        showHiddenFiles: sShowHiddenFiles,
        fileType: sPageDisplayed === FileBrowserPage.Namespaces ? 'all' : sFileTypeFilter,
        pageDisplayed: pageDisplayed,
        storageType: pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
        focus: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
    }
    //
  }; // handleNavigationSelection

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      /*sm={12}*/
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <div
        style={{
          padding: '0px',
          margin: '0px 0px 5px 0px',
          alignItems: 'center',
          height: 'auto',
          display: 'flex',
          flex: '0 0 38px',
          flexDirection: 'row',
          position: 'relative',
        }}
      >
        <div
          role="navigation"
          aria-label="Navigate the file browser"
          className="text-colour"
          style={{
            flex: '1 1',
            height: 'auto',
            margin: '0px 0px 0px 0px',
            padding: '0px 0px 0px 10px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {sG_narrowScreen === true && (
            <MiniButton
              className="text-colour"
              onClick={() => {
                switch (sPageDisplayed) {
                  case FileBrowserPage.Namespaces:
                  case FileBrowserPage.Collections:
                  case FileBrowserPage.JobDetails:
                  case FileBrowserPage.UserStorage:
                  case FileBrowserPage.ProjectStorage:
                    handleNavigationSelection('dataManagement');
                    break;
                  case FileBrowserPage.Dataset:
                  case FileBrowserPage.Items:
                    handleNavigationSelection('namespaces');
                    break;
                  case FileBrowserPage.Collection:
                    handleNavigationSelection('collections');
                    break;
                }
              }}
              aria-label={t('Up')}
              title={t('Up')}
              disabled={sPageDisplayed === FileBrowserPage.FileBrowser}
            >
              <FolderUp width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
          )}
          <NavigationNode
            text={t('File browser')}
            id="dataManagement"
            visible={
              sG_narrowScreen === false || sPageDisplayed === FileBrowserPage.FileBrowser
            }
            clickable={sPageDisplayed !== FileBrowserPage.FileBrowser}
            onActivate={handleNavigationSelection}
            hideSeparator={true}
          />
          <NavigationNode
            text={t('Namespaces')}
            id="namespaces"
            visible={
              sPageDisplayed === FileBrowserPage.Namespaces ||
              ([FileBrowserPage.Items, FileBrowserPage.Dataset].includes(
                sPageDisplayed,
              ) &&
                sG_narrowScreen === false)
            }
            clickable={sPageDisplayed !== FileBrowserPage.Namespaces}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          <NavigationNode
            text={t('Collections')}
            id="collections"
            visible={
              sPageDisplayed === FileBrowserPage.Collections ||
              (sPageDisplayed === FileBrowserPage.Collection && sG_narrowScreen === false)
            }
            clickable={sPageDisplayed !== FileBrowserPage.Collections}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          <NavigationNode
            text={
              t('Namespace').toUpperCase() +
              ': ' +
              (sNamespace !== undefined ? sNamespace.toUpperCase() : '')
            }
            id="items"
            visible={
              sPageDisplayed === FileBrowserPage.Items ||
              sPageDisplayed === FileBrowserPage.Dataset
            }
            clickable={sPageDisplayed !== FileBrowserPage.Items}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          <NavigationNode
            text={
              t('Dataset').toUpperCase() +
              ': ' +
              (sDataset !== undefined ? sDataset.toUpperCase() : '')
            }
            id="dataset"
            visible={sPageDisplayed === FileBrowserPage.Dataset}
            clickable={sPageDisplayed !== FileBrowserPage.Dataset}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          <NavigationNode
            text={
              t('Job Details').toUpperCase() +
              ': ' +
              (sJobID !== undefined ? sJobID.toUpperCase() : '')
            }
            id="jobDetails"
            visible={sPageDisplayed === FileBrowserPage.JobDetails}
            clickable={sPageDisplayed !== FileBrowserPage.JobDetails}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          <NavigationNode
            text={
              t('Collection').toUpperCase() +
              ': ' +
              (sCollection !== undefined ? sCollection.name.toUpperCase() : '')
            }
            id="collection"
            visible={sPageDisplayed === FileBrowserPage.Collection}
            clickable={sPageDisplayed !== FileBrowserPage.Collection}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          <NavigationNode
            text={sG_username === undefined ? 'HOME' : sG_username.toUpperCase()}
            id="userStorage_0"
            visible={sPageDisplayed === FileBrowserPage.UserStorage}
            clickable={sStoragePath.length > 0}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          {sStoragePath.map((item, index) => (
            <NavigationNode
              text={item.toUpperCase()}
              id={'userStorage_' + (index + 1).toString()}
              visible={sPageDisplayed === FileBrowserPage.UserStorage}
              clickable={sStoragePath.length > index + 1}
              onActivate={handleNavigationSelection}
            />
          ))}
          <NavigationNode
            text={`Project (${sG_project?.name === undefined ? 'HOME' : sG_project?.name.toUpperCase()})`}
            id="projectStorage_0"
            visible={sPageDisplayed === FileBrowserPage.ProjectStorage}
            clickable={sStoragePath.length > 0}
            onActivate={handleNavigationSelection}
            hideSeparator={sG_narrowScreen}
          />
          {sStoragePath.map((item, index) => (
            <NavigationNode
              text={item.toUpperCase()}
              id={'projectStorage_' + (index + 1).toString()}
              visible={sPageDisplayed === FileBrowserPage.ProjectStorage}
              clickable={sStoragePath.length > index + 1}
              onActivate={handleNavigationSelection}
            />
          ))}
          <div style={{ flex: '1 1' }} />
        </div>
        {sSiteExpanded === false && sG_narrowScreen && (
          <button
            type="button"
            style={{
              border: 'none',
              background: 'transparent',
              paddingRight: '10px',
            }}
            title="Change site"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              dispatch(setSiteExpanded(true));
              e.stopPropagation();
            }}
            disabled={
              sPageDisplayed !== FileBrowserPage.UserStorage &&
              sPageDisplayed !== FileBrowserPage.ProjectStorage &&
              sPageDisplayed !== FileBrowserPage.FileBrowser
            }
          >
            <SiteIcon site={sG_site} size={30} />
          </button>
        )}
        {sG_narrowScreen === false && (
          <div
            style={{
              flex: '0 0 auto',
              display: 'flex',
              margin: '0px 10px 0px 0px',
            }}
          >
            <label id="file-browser-site" className="text-colour">
              Site:
            </label>
          </div>
        )}
        {(sSiteExpanded || sG_narrowScreen === false) && (
          <div
            className="window-background text-colour"
            style={{
              flex: '0 0 180px',
              margin: '0px 5px 0px 0px',
              position: sG_narrowScreen === true ? 'absolute' : 'static',
              right: '0px',
              zIndex: '3',
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
            }}
          >
            <EditableSelect
              aria-labelledby="file-browser-site"
              gatewayTheme={sG_darkMode ? 'dark' : 'light'}
              styles={sG_darkMode ? customStylesDark : customStylesLight}
              isDisabled={
                sPageDisplayed !== FileBrowserPage.UserStorage &&
                sPageDisplayed !== FileBrowserPage.ProjectStorage &&
                sPageDisplayed !== FileBrowserPage.FileBrowser
              }
              isSearchable={false}
              options={_siteOptions}
              placeholder={t('Site')}
              value={_siteOptions.find((site) => site.value === sG_site?.site_uuid)}
              onChange={(
                selectedOption: {
                  value: string | number;
                  label: string;
                  icon?: string;
                } | null,
              ) => {
                dispatch(
                  rG_setSite(
                    sD_sites.find((find) => find.site_uuid === selectedOption?.value) ??
                      {},
                  ),
                );
                dispatch(setSiteExpanded(false));
              }}
              isClearable={false}
              showSiteIcon={true}
            />
          </div>
        )}
      </div>

      <div
        ref={dataWindow}
        id="file-browser-whole-window"
        style={{
          flex: '1 1',
          display: 'flex',
          flexDirection: 'row',
          padding: '0px 0px 0px 0px',
          margin: '0px 0px 0px 0px',
          width: '100%',
          position: 'relative',
        }}
        onClick={onClickDivHandler}
      >
        {/*
        /// main file browser section.
        */}
        <div
          style={{
            flex: '1 1',
            margin: '0px 10px 0px 0px',
            padding: '0px 0px 0px 0px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DataManagementTable />
        </div>

        <CollapsingPanel
          className="br-d-l"
          position={PanelPosition.RIGHT}
          title={t('Staging') + ' (' + sSelectedItems.length.toString() + ')'}
          hideTitle={true}
          height="100%"
          width="370px"
        >
          <DetailsPane
            getLocations={
              sPageDisplayed === FileBrowserPage.Items ||
              sPageDisplayed === FileBrowserPage.Dataset ||
              sPageDisplayed === FileBrowserPage.JobDetails ||
              sPageDisplayed === FileBrowserPage.Collection
            }
          />
          <div style={{ flex: '0 0 10px' }} />

          <label
            id="file-browser-lifetime-label"
            className="text-colour"
            style={{ margin: '0px 0px 0px 10px' }}
          >
            Staging lifetime [s]:
          </label>
          <div style={{ flex: '0 0 5px' }} />
          <div className="padding" data-padding="10px" style={{ alignItems: 'center' }}>
            <input
              className="input-field"
              aria-labelledby="file-browser-lifetime-label"
              type="text"
              name="lifetime"
              onKeyPress={numericKeysOnly}
              placeholder={t('Lifetime [s]')}
              onChange={inputHandler}
              onBlur={onBlurEvent}
              maxLength={50}
              value={sLifetimeStr}
              disabled={
                sPageDisplayed === FileBrowserPage.UserStorage ||
                sPageDisplayed === FileBrowserPage.ProjectStorage
              }
              data-disabled={
                sPageDisplayed === FileBrowserPage.UserStorage ||
                sPageDisplayed === FileBrowserPage.ProjectStorage
                  ? 'T'
                  : 'F'
              }
            />
          </div>
          <div style={{ flex: '0 0 5px' }} />
          <div
            style={{
              width: '100%',
              flex: '0 0 auto',
            }}
          >
            <CheckBox
              name="showHiddenFiles"
              text={t('Show hidden files')}
              value={t('Show hidden files')}
              checked={sShowHiddenFiles === true}
              onChange={inputHandler}
              margin={15}
              enabled={
                sPageDisplayed === FileBrowserPage.UserStorage ||
                sPageDisplayed === FileBrowserPage.ProjectStorage
              }
            />
          </div>
        </CollapsingPanel>
        <ContextMenu
          key={sContextMenuRefresh.toString()}
          system={CONTEXT_SYSTEMS.FILE_BROWSER_STAGING}
        />
      </div>
    </div>
  );
} // DataManagement

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Displays details of the selected file(s)
//
//	------------------------------------------------------------

function DetailsPane(props: { getLocations: boolean }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // page currently displayed.
  const sPageDisplayed: FileBrowserPage = useAppSelector(
    (state) => state.fileBrowser.pageParams.pageDisplayed,
  );

  // selected items.
  const sSelectedItems: DataItemType[] = useAppSelector(
    (state) => state.fileBrowser.selectedItems,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //	------------------------------------------------------------
  //
  //	Handler button clicks.
  //
  //	------------------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // prevent propogation of the onClick event.
    event.stopPropagation();

    // stage the data to a specific site.
    if (button.name === 'stage-to') {
      //
      // if the menu is currently displayed then hide it.
      if (
        ctx_menuDisplayed({ contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING }) ===
        true
      ) {
        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
            visible: false,
          }),
        );
      } else {
        //
        // update the text in the main context menu.
        ctx_buildContextMenu({
          contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
          contextMenu: 1 /* SITES menu */,
          parentRowID: -1,
        });

        // check if this position is OK - use the button as a references point.
        ctx_calculatePosition({
          contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
          contextMenu: 1 /* SITES menu */,
          button: button.name,
        });

        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
            contextMenu: 1 /* SITES menu */,
            visible: true,
          }),
        );

        dispatch(
          ctx_setContextRow({
            contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
            row: -1,
          }),
        );

        // close user dropdown menu.
        dispatch(setUserDropdownMenuDisplayed(false));

        // refresh the context menu.
        dispatch(setContextMenuRefresh(Date.now()));

        // prevent propogation of the onClick event.
        event.stopPropagation();
        //
      }
      //
    }
    //
  }; // onClickBtnHandler

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // add up the files.
  var totalSize: number = 0.0;
  for (var i = 0; i < sSelectedItems.length; i++) {
    if ((sSelectedItems[i].bytes ?? -1) >= 0)
      totalSize = totalSize + (sSelectedItems[i].bytes ?? 0);
  }

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	get storage locations for the selected data item.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    // get a list of locations where this data item is stored.
    let selectedItems: DataItemType[] = sSelectedItems.filter((item) =>
      ['FILE', 'DATASET', 'CONTAINER'].includes(item.did_type?.toUpperCase() ?? ''),
    );

    if (props.getLocations) {
      //
      fnD_buildRucioLocations({
        items: selectedItems,
        catalog: 'file-browser',
      });
      //
    }
    //
  }, [sSelectedItems]);

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  var singleFileSelected: boolean = false;
  if (sSelectedItems.length === 1) {
    if (
      sSelectedItems[0].did_type?.toUpperCase() === 'FILE' ||
      sSelectedItems[0].did_type?.toUpperCase() === 'DATASET' ||
      sSelectedItems[0].did_type?.toUpperCase() === 'CONTAINER'
    ) {
      singleFileSelected = true;
    }
  }

  return (
    <div
      style={{
        flex: '1 1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'end',
        width: '100%',
      }}
    >
      <div
        className="text-colour"
        style={{
          flex: '0 0 auto',
          margin: '15px 10px 0px 0px',
          fontSize: '1.2em',
        }}
      >
        {sSelectedItems.length === 1
          ? sSelectedItems[0].filename
          : (sSelectedItems.length?.toString() ?? 0) + ' items selected'}
      </div>
      <div
        className="text-colour"
        style={{
          margin: '5px 10px 0px 15px',
        }}
      >
        Size: {displayFileSize({ bytes: totalSize })}
      </div>
      <div style={{ flex: '0 0 20px' }} />
      {sPageDisplayed === FileBrowserPage.Items && sSelectedItems.length > 0 && (
        <DataStagingComponent
          allItems={sSelectedItems}
          catalog="file-browser"
          contextSystem={CONTEXT_SYSTEMS.FILE_BROWSER_STAGING}
          contextMenuRefresh={setContextMenuRefresh}
          hideItemCount={true}
        />
      )}
    </div>
  );
} // DetailsPane

{
  /*<div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            margin: '0px 0px 10px 0px',
          }}
        >
          <ToolButton
            key="Stage to"
            name="stage-to"
            onClick={onClickBtnHandler}
            tooltip={t('Stage data items to a specific site')}
            text={t('Stage to') + '  ▸'}
            disabled={sSelectedItems.length === 0}
            type={ToolButtonType.SECONDARY}
          />
        </div>
      )}*/
}
//	------------------------------------------------------------
//
//	Displays a clickable navigation node so the user can
//	navigate the file browser.
//
//	------------------------------------------------------------

function NavigationNode(props: {
  text: string;
  id: string;
  visible: boolean;
  clickable: boolean;
  onActivate: (id: string) => void;
  hideSeparator?: boolean;
  disabled?: boolean;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.fileBrowser.loadingComponent,
  );

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  if (props.visible !== true) {
    return <></>;
  }

  const separator =
    props.hideSeparator !== true ? (
      <div
        style={{
          flex: '0 0 auto',
          margin: '0px 5px 0px 5px',
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
        }}
        className="navigation"
      >
        &gt;
      </div>
    ) : (
      <></>
    );

  const baseStyle: React.CSSProperties = {
    flex: '0 0 auto',
    height: 'auto',
    padding: '0px 10px 0px 10px',
    userSelect: 'none',
  };

  const content = props.text.toUpperCase();

  if (props.clickable === true) {
    return (
      <>
        {separator}
        <button
          type="button"
          id={props.id}
          className="navigation"
          data-clickable="T"
          data-disabled={sLoadingComponent ? 'F' : 'F'}
          onClick={() => props.onActivate(props.id)}
          onKeyDown={(event) =>
            handleKeyboardActivation(event, () => props.onActivate(props.id))
          }
          style={{ ...baseStyle, background: 'none', border: 'none', cursor: 'pointer' }}
          disabled={false /*sLoadingComponent*/}
        >
          {content}
        </button>
      </>
    );
  }

  return (
    <>
      {separator}
      <div
        id={props.id}
        className="navigation"
        data-clickable="F"
        style={baseStyle}
        aria-current="page"
      >
        {content}
      </div>
    </>
  );
} // NavigationNode
